import React, { useState, useRef, useEffect } from 'react'
import { useDroppable, useDndMonitor } from '@dnd-kit/core'
import FolderIcon from '@atlaskit/icon/glyph/folder'
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import MoreIcon from '@atlaskit/icon/glyph/more'
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler'
import Button from '@atlaskit/button'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'

/**
 * FOLDER ITEM COMPONENT
 *
 * Individual folder row in the folder tree.
 *
 * Features:
 * - Click row to select folder
 * - Click chevron to expand/collapse sub-folders
 * - Indentation based on nesting level
 * - Context menu for actions (rename, delete, add sub-folder)
 * - Droppable target for scenarios AND folders (full drag-and-drop support)
 */
function FolderItem({
  folder,
  level = 0,
  isExpanded = false,
  isSelected = false,
  hasChildren = false,
  onSelect,
  onToggleExpand,
  onRename,
  onDelete,
  onAddSubfolder,
  onClone,
  onImportFromLibrary,
  onImportFromRelease,
  dragHandleProps,
  isDragging = false
}) {
  const [isHovered, setIsHovered] = useState(false)
  // Track dropdown menu open state to prevent menu from disappearing
  // when mouse briefly leaves the folder row while navigating to dropdown items
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [dropPosition, setDropPosition] = useState(null) // 'top', 'center', 'bottom', or null
  const itemRef = useRef(null)

  // Make folder a droppable target for scenarios
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: `folder-drop-${folder.id}`,
    data: {
      type: 'folder',
      folderId: folder.id
    }
  })

  // Combine refs
  const setRefs = (node) => {
    setDroppableRef(node)
    itemRef.current = node
  }

  // Monitor drag events to determine drop position (top/center/bottom)
  useDndMonitor({
    onDragMove(event) {
      if (!isOver || !itemRef.current) {
        setDropPosition(null)
        return
      }

      // Only show position indicators when dragging a folder
      const activeId = String(event.active.id)
      if (!activeId.startsWith('folder-') || activeId.startsWith('folder-drop-')) {
        setDropPosition('center') // Scenarios always go to center
        return
      }

      // Calculate position within the folder item
      const rect = itemRef.current.getBoundingClientRect()
      const y = event.activatorEvent?.clientY + (event.delta?.y || 0)

      if (!y) {
        setDropPosition('center')
        return
      }

      const relativeY = y - rect.top
      const percentY = relativeY / rect.height

      // Top 30% = insert before, Bottom 30% = insert after, Center = nest
      if (percentY < 0.3) {
        setDropPosition('top')
      } else if (percentY > 0.7) {
        setDropPosition('bottom')
      } else {
        setDropPosition('center')
      }
    },
    onDragEnd() {
      setDropPosition(null)
    },
    onDragCancel() {
      setDropPosition(null)
    }
  })

  // Reset drop position when not over
  useEffect(() => {
    if (!isOver) {
      setDropPosition(null)
    }
  }, [isOver])

  // Calculate left padding based on nesting level
  // Reduced padding for better space utilization with deep hierarchies
  const leftPadding = 4 + (level * 12) // 12px indent per level

  // Determine colors based on state (including drop target highlight)
  // Only show center highlight when dropping in center zone
  const showCenterHighlight = isOver && dropPosition === 'center'
  // Keep hover appearance while dropdown menu is open
  const showHovered = isHovered || isMenuOpen
  const backgroundColor = showCenterHighlight
    ? 'var(--ds-background-selected-hovered, #B3D4FF)' // Highlight when nesting
    : isSelected
      ? 'var(--ds-background-selected, #DEEBFF)'
      : showHovered
        ? 'var(--ds-background-neutral-hovered, #EBECF0)'
        : 'transparent'

  const textColor = isSelected
    ? 'var(--ds-text-selected, #0052CC)'
    : 'var(--ds-text, #172B4D)'

  const iconColor = isSelected
    ? 'var(--ds-icon-selected, #0052CC)'
    : 'var(--ds-icon, #6B778C)'

  // Border style for center drop target feedback (nesting)
  const borderStyle = showCenterHighlight
    ? '2px dashed var(--ds-border-focused, #4C9AFF)'
    : '2px solid transparent'

  // Insertion line indicator color
  const insertionLineColor = 'var(--ds-border-focused, #4C9AFF)'

  return (
    <div
      ref={setRefs}
      className={`folder-item ${isSelected ? 'selected' : ''} ${isOver ? 'drop-target' : ''}`}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        padding: `4px 8px 4px ${leftPadding}px`,
        borderRadius: '3px',
        cursor: 'pointer',
        backgroundColor,
        marginBottom: '1px',
        transition: 'background-color 0.15s ease, border 0.15s ease',
        border: borderStyle
      }}
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top insertion line indicator (for reordering - insert before) */}
      {isOver && dropPosition === 'top' && (
        <div
          style={{
            position: 'absolute',
            top: '-2px',
            left: '8px',
            right: '8px',
            height: '3px',
            backgroundColor: insertionLineColor,
            borderRadius: '2px',
            zIndex: 10
          }}
        />
      )}

      {/* Bottom insertion line indicator (for reordering - insert after) */}
      {isOver && dropPosition === 'bottom' && (
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            left: '8px',
            right: '8px',
            height: '3px',
            backgroundColor: insertionLineColor,
            borderRadius: '2px',
            zIndex: 10
          }}
        />
      )}
      {/* Drag Handle (visible on hover when dragHandleProps provided) */}
      {dragHandleProps && (
        <div
          {...dragHandleProps}
          className="drag-handle"
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            opacity: (isHovered || isMenuOpen) ? 1 : 0,
            transition: 'opacity 0.15s ease',
            touchAction: 'none',
            marginRight: '0px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragHandlerIcon
            label="Drag to reorder"
            size="small"
            primaryColor="var(--ds-text-subtlest, #6B778C)"
          />
        </div>
      )}

      {/* Expand/Collapse Chevron */}
      <div
        className="folder-chevron"
        style={{
          width: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: hasChildren ? 1 : 0,
          cursor: hasChildren ? 'pointer' : 'default'
        }}
        onClick={hasChildren ? onToggleExpand : undefined}
      >
        {hasChildren && (
          isExpanded ? (
            <ChevronDownIcon label="Collapse" size="small" primaryColor={iconColor} />
          ) : (
            <ChevronRightIcon label="Expand" size="small" primaryColor={iconColor} />
          )
        )}
      </div>

      {/* Folder Icon */}
      <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
        {isSelected ? (
          <FolderFilledIcon label="Folder" size="small" primaryColor={iconColor} />
        ) : (
          <FolderIcon label="Folder" size="small" primaryColor={iconColor} />
        )}
      </div>

      {/* Folder Name - uses --ds-font-body token to match details page typography */}
      <span style={{
        flex: 1,
        font: 'var(--ds-font-body)',
        fontWeight: isSelected ? 600 : 400,
        letterSpacing: '-0.003em',
        color: textColor,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {folder.name}
      </span>

      {/* Scenario Count Badge */}
      {folder.testScenarioCount > 0 && (
        <span style={{
          fontSize: '11px',
          color: 'var(--ds-text-subtlest, #6B778C)',
          backgroundColor: 'var(--ds-background-neutral, #DFE1E6)',
          padding: '1px 5px',
          borderRadius: '8px',
          marginRight: '4px'
        }}>
          {folder.testScenarioCount}
        </span>
      )}

      {/* More Actions Menu - always rendered, visibility controlled by opacity/pointerEvents
         to prevent unmounting when mouse briefly leaves the row while dropdown is open */}
      <div
        className="folder-actions"
        onClick={(e) => e.stopPropagation()}
        style={{
          marginLeft: '4px',
          opacity: (isHovered || isMenuOpen) ? 1 : 0,
          transition: 'opacity 0.15s ease',
          pointerEvents: (isHovered || isMenuOpen) ? 'auto' : 'none'
        }}
      >
        <DropdownMenu
          trigger={({ triggerRef, ...props }) => (
            <Button
              {...props}
              ref={triggerRef}
              appearance="subtle"
              spacing="compact"
              iconBefore={<MoreIcon label="More actions" size="small" />}
              style={{ padding: '2px' }}
            />
          )}
          shouldRenderToParent
          onOpenChange={({ isOpen }) => setIsMenuOpen(isOpen)}
        >
          <DropdownItemGroup>
            <DropdownItem onClick={() => onAddSubfolder?.(folder.id)}>
              Add Sub-folder
            </DropdownItem>
            {onImportFromLibrary && (
              <DropdownItem onClick={() => onImportFromLibrary?.(folder.id)}>
                Import from Library
              </DropdownItem>
            )}
            {onImportFromRelease && (
              <DropdownItem onClick={() => onImportFromRelease?.(folder.id)}>
                Import from Release
              </DropdownItem>
            )}
            <DropdownItem onClick={() => onClone?.(folder.id)}>
              Clone
            </DropdownItem>
            <DropdownItem onClick={() => onRename?.(folder.id)}>
              Rename
            </DropdownItem>
            <DropdownItem onClick={() => onDelete?.(folder.id)}>
              Delete
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      </div>
    </div>
  )
}

export { FolderItem }
